/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { Antifraud } from '../models/Antifraud';
import type { AuditLogDetail } from '../models/AuditLogDetail';
import type { CaseRCAEDetail } from '../models/CaseRCAEDetail';
import type { ClaimDebitNotesDetail } from '../models/ClaimDebitNotesDetail';
import type { ClaimDebitNotesPaymentOrderWrite } from '../models/ClaimDebitNotesPaymentOrderWrite';
import type { ClaimDebitNotesWrite } from '../models/ClaimDebitNotesWrite';
import type { ClaimDocumentDetail } from '../models/ClaimDocumentDetail';
import type { ClaimDocumentWrite } from '../models/ClaimDocumentWrite';
import type { ClaimParty } from '../models/ClaimParty';
import type { ClaimRCAICaseDetail } from '../models/ClaimRCAICaseDetail';
import type { CoreCaseReserveWrite } from '../models/CoreCaseReserveWrite';
import type { CreateFromNotification } from '../models/CreateFromNotification';
import type { DamageReserve } from '../models/DamageReserve';
import type { GreenCardLookupRequest } from '../models/GreenCardLookupRequest';
import type { GreenCardLookupResponse } from '../models/GreenCardLookupResponse';
import type { New } from '../models/New';
import type { PaginatedAuditLogList } from '../models/PaginatedAuditLogList';
import type { PaginatedCaseRCAEListList } from '../models/PaginatedCaseRCAEListList';
import type { PaginatedClaimDebitNotesList } from '../models/PaginatedClaimDebitNotesList';
import type { PaginatedClaimDocumentList } from '../models/PaginatedClaimDocumentList';
import type { PaginatedClaimRCAICaseList } from '../models/PaginatedClaimRCAICaseList';
import type { PaginatedRCAIEvaluationList } from '../models/PaginatedRCAIEvaluationList';
import type { PaginatedRCAIExaminationList } from '../models/PaginatedRCAIExaminationList';
import type { PaginatedRepairStationListList } from '../models/PaginatedRepairStationListList';
import type { PatchedClaimDebitNotesWrite } from '../models/PatchedClaimDebitNotesWrite';
import type { PatchedClaimPartyWrite } from '../models/PatchedClaimPartyWrite';
import type { PatchedRepairStation } from '../models/PatchedRepairStation';
import type { PatchedRequest } from '../models/PatchedRequest';
import type { RCAIEvaluationDetail } from '../models/RCAIEvaluationDetail';
import type { RCAIExaminationDetail } from '../models/RCAIExaminationDetail';
import type { RepairStation } from '../models/RepairStation';
import type { RepairStationDetail } from '../models/RepairStationDetail';
import type { RepairStationModify } from '../models/RepairStationModify';
import type { Suspend } from '../models/Suspend';
import type { Transition } from '../models/Transition';
import type { ValidationError } from '../models/ValidationError';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ClaimsService {

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param accidentCountry 
   * @param accidentDate 
   * @param accidentDateGte 
   * @param accidentDateLte 
   * @param accidentReport * `amicable_settlement` - Constatare amiabilă
 * * `police_report` - Raportul poliției
   * @param greenCardDocument ID from RCA API
   * @param insured 
   * @param number 
   * @param ordering Ordering fields: created, number, policy_number, id, accident_date
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: number, policy_number, insured_name, insured_identification_number, vehicle_registration, parties__person__as_person__email, parties__person__as_person__first_name, parties__person__as_person__last_name, parties__person__as_person__identification_number, parties__person__as_legalentity__identification_number, parties__person__as_legalentity__name, parties__vehicle__certificate_number, parties__vehicle__registration_number
   * @param status * `new` - Nou
 * * `documents_gathering` - Colectare documente
 * * `estimate` - Deviz de calcul
 * * `approval` - Aprobare
 * * `adjustments` - Necesită ajustări
 * * `director_approval` - Aprobare director
 * * `payment_order` - Ordin de plată
 * * `payment` - Plată
 * * `paid` - Plătit
 * * `closed` - Închis
   * @returns PaginatedCaseRCAEListList 
   * @throws ApiError
   */
  public static claimsCaseRcaeList(
accidentCountry?: string,
accidentDate?: string,
accidentDateGte?: string,
accidentDateLte?: string,
accidentReport?: 'amicable_settlement' | 'police_report' | null,
greenCardDocument?: number,
insured?: number,
number?: string,
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
status?: Array<'adjustments' | 'approval' | 'closed' | 'director_approval' | 'documents_gathering' | 'estimate' | 'new' | 'paid' | 'payment' | 'payment_order'>,
): CancelablePromise<PaginatedCaseRCAEListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/',
      query: {
        'accident_country': accidentCountry,
        'accident_date': accidentDate,
        'accident_date_gte': accidentDateGte,
        'accident_date_lte': accidentDateLte,
        'accident_report': accidentReport,
        'green_card_document': greenCardDocument,
        'insured': insured,
        'number': number,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
        'status': status,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param casePk 
   * @param ordering Ordering fields: created, status, amount
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedClaimDebitNotesList 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesList(
casePk: string,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedClaimDebitNotesList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/debit-notes/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * Create debit note
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param casePk 
   * @param formData 
   * @returns ClaimDebitNotesDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesCreate(
casePk: string,
formData: ClaimDebitNotesWrite,
): CancelablePromise<ClaimDebitNotesDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcae/{case_pk}/debit-notes/',
      path: {
        'case_pk': casePk,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param casePk 
   * @param id A unique integer value identifying this claim debit notes.
   * @returns ClaimDebitNotesDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesRetrieve(
casePk: string,
id: number,
): CancelablePromise<ClaimDebitNotesDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/debit-notes/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * Update debit note
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param casePk 
   * @param id A unique integer value identifying this claim debit notes.
   * @param formData 
   * @returns ClaimDebitNotesDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesPartialUpdate(
casePk: string,
id: number,
formData?: PatchedClaimDebitNotesWrite,
): CancelablePromise<ClaimDebitNotesDetail> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/claims/case-rcae/{case_pk}/debit-notes/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * Remove debit note
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param casePk 
   * @param id A unique integer value identifying this claim debit notes.
   * @returns void 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesDestroy(
casePk: string,
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/claims/case-rcae/{case_pk}/debit-notes/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
      errors: {
        404: `Debit note not found`,
      },
    });
  }

  /**
   * Mark debit note as paid
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param casePk 
   * @param id A unique integer value identifying this claim debit notes.
   * @returns ClaimDebitNotesDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesMarkAsPaidCreate(
casePk: string,
id: number,
): CancelablePromise<ClaimDebitNotesDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcae/{case_pk}/debit-notes/{id}/mark-as-paid/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * Generate payment order PDF
   * GET .../debit-notes/{pk}/payment-order-pdf/ — Ordin privind achitarea despăgubirii (RO), per debit note.
   * @param casePk 
   * @param id A unique integer value identifying this claim debit notes.
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesPaymentOrderPdfRetrieve(
casePk: string,
id: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/debit-notes/{id}/payment-order-pdf/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * Upload payment order
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param casePk 
   * @param id A unique integer value identifying this claim debit notes.
   * @param formData 
   * @returns ClaimDebitNotesDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeDebitNotesUploadPaymentOrderCreate(
casePk: string,
id: number,
formData: ClaimDebitNotesPaymentOrderWrite,
): CancelablePromise<ClaimDebitNotesDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcae/{case_pk}/debit-notes/{id}/upload-payment-order/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param excludeType * `case` - Dosar
 * * `insurance_claim` - Cerere despăgubire
 * * `accident_report` - Documentul accidentului
 * * `photo` - Fotografie
 * * `other` - Alte
 * * `culprit_document` - Act de identitate al vinovatului
 * * `culprit_representative_document` - Act de identitate al reprezentantului vinovatului
 * * `culprit_power_of_attorney` - Procura vinovatului
 * * `culprit_driver_license` - Permis de conducere al vinovatului
 * * `culprit_registration_certificate` - Certificat de înregistrare a vinovatului
 * * `culprit_technical_inspection_report` - Raport de inspecție tehnică periodică al vinovatului
 * * `claimant_document` - Act de identitate al păgubitului
 * * `claimant_representative_document` - Act de identitate al reprezentantului păgubitului
 * * `claimant_power_of_attorney` - Procura păgubitului
 * * `claimant_driver_license` - Permis de conducere al păgubitului
 * * `claimant_registration_certificate` - Certificat de înregistrare a păgubitului
 * * `request_for_additional_documents` - Solicitare acte suplimentare
 * * `culprit_explication` - Explicația vinovatului
 * * `damage_assessment_report` - Proces-verbal de constatare a pagubelor
 * * `debit_notes` - Debit Notes
 * * `examination_report` - Procesul verbal de examinare
 * * `additional_examination_report` - Document de examinare suplimentar
 * * `additional_findings` - Constatări suplimentare
 * * `repair_station_bill` - Сontul spre plată de la stația de reparații
 * * `estimate` - Deviz de calcul
 * * `medical_document` - Document medical
 * * `expertise` - Expertiză
 * * `invoice` - Factura
 * * `compensation_estimate` - Deviz de despăgubire
 * * `indemnification_agreement` - Acord de despăgubire
 * * `terminate` - Ordin de terminare
 * * `suspend` - Ordin de suspendare
 * * `compensation_order` - Ordin de despăgubire
 * * `compensation_agreement` - Acord de despăgubire
 * * `payment_order` - Ordin de achitare
   * @param lastVersionOnly Filter only last version or unversioned documents
   * @param ordering Ordering fields: created, file__name, type
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param type * `case` - Dosar
 * * `insurance_claim` - Cerere despăgubire
 * * `accident_report` - Documentul accidentului
 * * `photo` - Fotografie
 * * `other` - Alte
 * * `culprit_document` - Act de identitate al vinovatului
 * * `culprit_representative_document` - Act de identitate al reprezentantului vinovatului
 * * `culprit_power_of_attorney` - Procura vinovatului
 * * `culprit_driver_license` - Permis de conducere al vinovatului
 * * `culprit_registration_certificate` - Certificat de înregistrare a vinovatului
 * * `culprit_technical_inspection_report` - Raport de inspecție tehnică periodică al vinovatului
 * * `claimant_document` - Act de identitate al păgubitului
 * * `claimant_representative_document` - Act de identitate al reprezentantului păgubitului
 * * `claimant_power_of_attorney` - Procura păgubitului
 * * `claimant_driver_license` - Permis de conducere al păgubitului
 * * `claimant_registration_certificate` - Certificat de înregistrare a păgubitului
 * * `request_for_additional_documents` - Solicitare acte suplimentare
 * * `culprit_explication` - Explicația vinovatului
 * * `damage_assessment_report` - Proces-verbal de constatare a pagubelor
 * * `debit_notes` - Debit Notes
 * * `examination_report` - Procesul verbal de examinare
 * * `additional_examination_report` - Document de examinare suplimentar
 * * `additional_findings` - Constatări suplimentare
 * * `repair_station_bill` - Сontul spre plată de la stația de reparații
 * * `estimate` - Deviz de calcul
 * * `medical_document` - Document medical
 * * `expertise` - Expertiză
 * * `invoice` - Factura
 * * `compensation_estimate` - Deviz de despăgubire
 * * `indemnification_agreement` - Acord de despăgubire
 * * `terminate` - Ordin de terminare
 * * `suspend` - Ordin de suspendare
 * * `compensation_order` - Ordin de despăgubire
 * * `compensation_agreement` - Acord de despăgubire
 * * `payment_order` - Ordin de achitare
   * @returns PaginatedClaimDocumentList 
   * @throws ApiError
   */
  public static claimsCaseRcaeDocumentsList(
casePk: string,
excludeType?: Array<'accident_report' | 'additional_examination_report' | 'additional_findings' | 'case' | 'claimant_document' | 'claimant_driver_license' | 'claimant_power_of_attorney' | 'claimant_registration_certificate' | 'claimant_representative_document' | 'compensation_agreement' | 'compensation_estimate' | 'compensation_order' | 'culprit_document' | 'culprit_driver_license' | 'culprit_explication' | 'culprit_power_of_attorney' | 'culprit_registration_certificate' | 'culprit_representative_document' | 'culprit_technical_inspection_report' | 'damage_assessment_report' | 'debit_notes' | 'estimate' | 'examination_report' | 'expertise' | 'indemnification_agreement' | 'insurance_claim' | 'invoice' | 'medical_document' | 'other' | 'payment_order' | 'photo' | 'repair_station_bill' | 'request_for_additional_documents' | 'suspend' | 'terminate'>,
lastVersionOnly?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
type?: Array<'accident_report' | 'additional_examination_report' | 'additional_findings' | 'case' | 'claimant_document' | 'claimant_driver_license' | 'claimant_power_of_attorney' | 'claimant_registration_certificate' | 'claimant_representative_document' | 'compensation_agreement' | 'compensation_estimate' | 'compensation_order' | 'culprit_document' | 'culprit_driver_license' | 'culprit_explication' | 'culprit_power_of_attorney' | 'culprit_registration_certificate' | 'culprit_representative_document' | 'culprit_technical_inspection_report' | 'damage_assessment_report' | 'debit_notes' | 'estimate' | 'examination_report' | 'expertise' | 'indemnification_agreement' | 'insurance_claim' | 'invoice' | 'medical_document' | 'other' | 'payment_order' | 'photo' | 'repair_station_bill' | 'request_for_additional_documents' | 'suspend' | 'terminate'>,
): CancelablePromise<PaginatedClaimDocumentList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/documents/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'exclude_type': excludeType,
        'last_version_only': lastVersionOnly,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'type': type,
      },
    });
  }

  /**
   * Create claim document
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param formData 
   * @returns ClaimDocumentDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeDocumentsCreate(
casePk: string,
formData: ClaimDocumentWrite,
): CancelablePromise<ClaimDocumentDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcae/{case_pk}/documents/',
      path: {
        'case_pk': casePk,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param id A unique integer value identifying this claim document.
   * @returns ClaimDocumentDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeDocumentsRetrieve(
casePk: string,
id: number,
): CancelablePromise<ClaimDocumentDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/documents/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * Remove claim document. Only unversioned documents can be removed.
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param id A unique integer value identifying this claim document.
   * @returns void 
   * @throws ApiError
   */
  public static claimsCaseRcaeDocumentsDestroy(
casePk: string,
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/claims/case-rcae/{case_pk}/documents/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
      errors: {
        404: `Document not found`,
      },
    });
  }

  /**
   * Events history
   * @param casePk 
   * @param actorType * `user` - User
 * * `broker_user` - Broker User
 * * `system` - System
 * * `api` - External API
   * @param eventType 
   * @param ordering Ordering fields: created, event_type
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedAuditLogList 
   * @throws ApiError
   */
  public static claimsCaseRcaeHistoryList(
casePk: string,
actorType?: 'api' | 'broker_user' | 'system' | 'user',
eventType?: string,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedAuditLogList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/history/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'actor_type': actorType,
        'event_type': eventType,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * Events history
   * @param casePk 
   * @param id A unique integer value identifying this audit log.
   * @returns AuditLogDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeHistoryRetrieve(
casePk: string,
id: number,
): CancelablePromise<AuditLogDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/history/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * List claim parties for a case
   * Base viewset for claim parties. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @returns ClaimParty 
   * @throws ApiError
   */
  public static claimsCaseRcaePartiesList(
casePk: string,
): CancelablePromise<Array<ClaimParty>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/parties/',
      path: {
        'case_pk': casePk,
      },
    });
  }

  /**
   * Upsert claim party by role
   * Creates or replaces the culprit/claimant party for a claim. Always returns 200.
   * @param casePk 
   * @param role Party role
   * @param requestBody 
   * @returns ClaimParty 
   * @throws ApiError
   */
  public static claimsCaseRcaePartiesPartialUpdate(
casePk: string,
role: 'claimant' | 'culprit',
requestBody?: PatchedClaimPartyWrite,
): CancelablePromise<ClaimParty> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/claims/case-rcae/{case_pk}/parties/{role}/',
      path: {
        'case_pk': casePk,
        'role': role,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Remove claim party by role
   * DELETE /…/parties/culprit/delete/ or /claimant/delete/
   * @param casePk 
   * @param role Party role
   * @returns void 
   * @throws ApiError
   */
  public static claimsCaseRcaePartiesDeleteDestroy(
casePk: string,
role: 'claimant' | 'culprit',
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/claims/case-rcae/{case_pk}/parties/{role}/delete/',
      path: {
        'case_pk': casePk,
        'role': role,
      },
      errors: {
        404: `Party not found`,
      },
    });
  }

  /**
   * CaseRCAE reserves
   * @param casePk 
   * @param status * `active` - Active
 * * `deactivated` - Deactivated
 * * `paid` - Paid
   * @returns DamageReserve 
   * @throws ApiError
   */
  public static claimsCaseRcaeReservesList(
casePk: string,
status?: Array<'active' | 'deactivated' | 'paid'>,
): CancelablePromise<Array<DamageReserve>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{case_pk}/reserves/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'status': status,
      },
    });
  }

  /**
   * CaseRCAE reserves
   * @param casePk 
   * @param requestBody 
   * @param status * `active` - Active
 * * `deactivated` - Deactivated
 * * `paid` - Paid
   * @returns DamageReserve 
   * @throws ApiError
   */
  public static claimsCaseRcaeReservesUpdateUpdate(
casePk: string,
requestBody: CoreCaseReserveWrite,
status?: Array<'active' | 'deactivated' | 'paid'>,
): CancelablePromise<Array<DamageReserve>> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/claims/case-rcae/{case_pk}/reserves/update/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'status': status,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcae.
   * @returns CaseRCAEDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeRetrieve(
id: number,
): CancelablePromise<CaseRCAEDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * PATCH /:resource/:id/apply-status/ — Status transition.
   * @param id A unique integer value identifying this case rcae.
   * @param requestBody 
   * @returns CaseRCAEDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeApplyStatusPartialUpdate(
id: number,
requestBody?: PatchedRequest,
): CancelablePromise<CaseRCAEDetail> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/claims/case-rcae/{id}/apply-status/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * GET /claims/case-rcae/{id}/calculation-estimate-pdf/ — deviz (calcul) (RO).
   * @param id A unique integer value identifying this case rcae.
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaeCalculationEstimatePdfRetrieve(
id: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{id}/calculation-estimate-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcae.
   * @returns Transition 
   * @throws ApiError
   */
  public static claimsCaseRcaeCaseTransitionsList(
id: number,
): CancelablePromise<Array<Transition>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{id}/case-transitions/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * GET /claims/case-rcae/{id}/confirmation-letter-pdf/ — coverage confirmation letter (RO/EN).
   * @param id A unique integer value identifying this case rcae.
   * @param language * `ro` - ro
 * * `en` - en
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaeConfirmationLetterPdfRetrieve(
id: number,
language: 'ro' | 'en' = 'ro',
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{id}/confirmation-letter-pdf/',
      path: {
        'id': id,
      },
      query: {
        'language': language,
      },
    });
  }

  /**
   * GET /claims/case-rcae/{id}/damage-assessment-report-pdf/ — proces-verbal de constatare a pagubelor (RO).
   * @param id A unique integer value identifying this case rcae.
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaeDamageAssessmentReportPdfRetrieve(
id: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{id}/damage-assessment-report-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * GET /claims/case-rcae/{id}/request-for-additional-documents-pdf/ — request for additional documents (RO).
   * @param id A unique integer value identifying this case rcae.
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaeRequestForAdditionalDocumentsPdfRetrieve(
id: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{id}/request-for-additional-documents-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * GET /:resource/:id/validate-transition/ — Validate transition to status.
   * @param id A unique integer value identifying this case rcae.
   * @param status 
   * @returns ValidationError 
   * @throws ApiError
   */
  public static claimsCaseRcaeValidateTransitionList(
id: number,
status: string,
): CancelablePromise<Array<ValidationError>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/{id}/validate-transition/',
      path: {
        'id': id,
      },
      query: {
        'status': status,
      },
    });
  }

  /**
   * Lookup Green Card policy by registration number or policy ID
   * Search for a Green Card policy using a single `search_value` field. If the value starts with `RCAE-` it is treated as a policy ID (fetched from SIA Asig API when not found locally). Otherwise it is treated as a vehicle registration number. `country` is the accident country code and selects the Green Card area.
   * @param requestBody 
   * @returns GreenCardLookupResponse 
   * @throws ApiError
   */
  public static claimsCaseRcaeLookupCreate(
requestBody: GreenCardLookupRequest,
): CancelablePromise<GreenCardLookupResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcae/lookup/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * POST /claims/case-rcae/new/ — create an RCAE case via the flow's INIT transition.
   * @param requestBody 
   * @returns CaseRCAEDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaeNewCreate(
requestBody: New,
): CancelablePromise<CaseRCAEDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcae/new/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @returns Transition 
   * @throws ApiError
   */
  public static claimsCaseRcaeTransitionsList(): CancelablePromise<Array<Transition>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcae/transitions/',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param accidentDateGte 
   * @param accidentDateLte 
   * @param accidentReport * `amicable_settlement` - Constatare amiabilă
 * * `police_report` - Raportul poliției
   * @param notification 
   * @param number 
   * @param ordering Ordering fields: created, number, policy_number, id, accident_date
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param rcaDocument ID from RCA API
   * @param search Search fields: number, policy_number, insured_name, insured_identification_number, vehicle_registration, parties__person__as_person__email, parties__person__as_person__first_name, parties__person__as_person__last_name, parties__person__as_person__identification_number, parties__person__as_legalentity__identification_number, parties__person__as_legalentity__name, parties__vehicle__certificate_number, parties__vehicle__registration_number
   * @param status * `new` - Nou
 * * `validation` - Validare
 * * `examination_scheduled` - Examinare programtă
 * * `examination_non_appearance` - Neprezentarea păgubitului
 * * `examination_completed` - Examinare a avut loc
 * * `examination_adjustment` - Necesiată ajusări
 * * `repair` - Reparație
 * * `evaluation` - Evaluare
 * * `personal_injury_evaluation` - Evaluare
 * * `material_damage_evaluation` - Evaluare
 * * `additional_findings` - Constatări suplimentare
 * * `re_examination` - Re-examinare
 * * `estimate` - Deviz de calcul
 * * `approval` - Aprobare
 * * `adjustments` - Necesiată ajusări
 * * `refused` - Refuzat
 * * `supervisor_approved` - Aprobat de supervizor
 * * `director_approval` - Aprobare director
 * * `payment_order` - Ordin de plată
 * * `payment` - Plată
 * * `paid` - Plătit
 * * `closed` - Închis
 * * `suspend` - Suspendat
   * @returns PaginatedClaimRCAICaseList 
   * @throws ApiError
   */
  public static claimsCaseRcaiList(
accidentDateGte?: string,
accidentDateLte?: string,
accidentReport?: 'amicable_settlement' | 'police_report' | null,
notification?: number,
number?: string,
ordering?: string,
page?: number,
pageSize?: number,
rcaDocument?: number,
search?: string,
status?: Array<'additional_findings' | 'adjustments' | 'approval' | 'closed' | 'director_approval' | 'estimate' | 'evaluation' | 'examination_adjustment' | 'examination_completed' | 'examination_non_appearance' | 'examination_scheduled' | 'material_damage_evaluation' | 'new' | 'paid' | 'payment' | 'payment_order' | 'personal_injury_evaluation' | 're_examination' | 'refused' | 'repair' | 'supervisor_approved' | 'suspend' | 'validation'>,
): CancelablePromise<PaginatedClaimRCAICaseList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/',
      query: {
        'accident_date_gte': accidentDateGte,
        'accident_date_lte': accidentDateLte,
        'accident_report': accidentReport,
        'notification': notification,
        'number': number,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'rca_document': rcaDocument,
        'search': search,
        'status': status,
      },
    });
  }

  /**
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param excludeType * `case` - Dosar
 * * `insurance_claim` - Cerere despăgubire
 * * `accident_report` - Documentul accidentului
 * * `photo` - Fotografie
 * * `other` - Alte
 * * `culprit_document` - Act de identitate al vinovatului
 * * `culprit_representative_document` - Act de identitate al reprezentantului vinovatului
 * * `culprit_power_of_attorney` - Procura vinovatului
 * * `culprit_driver_license` - Permis de conducere al vinovatului
 * * `culprit_registration_certificate` - Certificat de înregistrare a vinovatului
 * * `culprit_technical_inspection_report` - Raport de inspecție tehnică periodică al vinovatului
 * * `claimant_document` - Act de identitate al păgubitului
 * * `claimant_representative_document` - Act de identitate al reprezentantului păgubitului
 * * `claimant_power_of_attorney` - Procura păgubitului
 * * `claimant_driver_license` - Permis de conducere al păgubitului
 * * `claimant_registration_certificate` - Certificat de înregistrare a păgubitului
 * * `request_for_additional_documents` - Solicitare acte suplimentare
 * * `culprit_explication` - Explicația vinovatului
 * * `damage_assessment_report` - Proces-verbal de constatare a pagubelor
 * * `debit_notes` - Debit Notes
 * * `examination_report` - Procesul verbal de examinare
 * * `additional_examination_report` - Document de examinare suplimentar
 * * `additional_findings` - Constatări suplimentare
 * * `repair_station_bill` - Сontul spre plată de la stația de reparații
 * * `estimate` - Deviz de calcul
 * * `medical_document` - Document medical
 * * `expertise` - Expertiză
 * * `invoice` - Factura
 * * `compensation_estimate` - Deviz de despăgubire
 * * `indemnification_agreement` - Acord de despăgubire
 * * `terminate` - Ordin de terminare
 * * `suspend` - Ordin de suspendare
 * * `compensation_order` - Ordin de despăgubire
 * * `compensation_agreement` - Acord de despăgubire
 * * `payment_order` - Ordin de achitare
   * @param lastVersionOnly Filter only last version or unversioned documents
   * @param ordering Ordering fields: created, file__name, type
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param type * `case` - Dosar
 * * `insurance_claim` - Cerere despăgubire
 * * `accident_report` - Documentul accidentului
 * * `photo` - Fotografie
 * * `other` - Alte
 * * `culprit_document` - Act de identitate al vinovatului
 * * `culprit_representative_document` - Act de identitate al reprezentantului vinovatului
 * * `culprit_power_of_attorney` - Procura vinovatului
 * * `culprit_driver_license` - Permis de conducere al vinovatului
 * * `culprit_registration_certificate` - Certificat de înregistrare a vinovatului
 * * `culprit_technical_inspection_report` - Raport de inspecție tehnică periodică al vinovatului
 * * `claimant_document` - Act de identitate al păgubitului
 * * `claimant_representative_document` - Act de identitate al reprezentantului păgubitului
 * * `claimant_power_of_attorney` - Procura păgubitului
 * * `claimant_driver_license` - Permis de conducere al păgubitului
 * * `claimant_registration_certificate` - Certificat de înregistrare a păgubitului
 * * `request_for_additional_documents` - Solicitare acte suplimentare
 * * `culprit_explication` - Explicația vinovatului
 * * `damage_assessment_report` - Proces-verbal de constatare a pagubelor
 * * `debit_notes` - Debit Notes
 * * `examination_report` - Procesul verbal de examinare
 * * `additional_examination_report` - Document de examinare suplimentar
 * * `additional_findings` - Constatări suplimentare
 * * `repair_station_bill` - Сontul spre plată de la stația de reparații
 * * `estimate` - Deviz de calcul
 * * `medical_document` - Document medical
 * * `expertise` - Expertiză
 * * `invoice` - Factura
 * * `compensation_estimate` - Deviz de despăgubire
 * * `indemnification_agreement` - Acord de despăgubire
 * * `terminate` - Ordin de terminare
 * * `suspend` - Ordin de suspendare
 * * `compensation_order` - Ordin de despăgubire
 * * `compensation_agreement` - Acord de despăgubire
 * * `payment_order` - Ordin de achitare
   * @returns PaginatedClaimDocumentList 
   * @throws ApiError
   */
  public static claimsCaseRcaiDocumentsList(
casePk: string,
excludeType?: Array<'accident_report' | 'additional_examination_report' | 'additional_findings' | 'case' | 'claimant_document' | 'claimant_driver_license' | 'claimant_power_of_attorney' | 'claimant_registration_certificate' | 'claimant_representative_document' | 'compensation_agreement' | 'compensation_estimate' | 'compensation_order' | 'culprit_document' | 'culprit_driver_license' | 'culprit_explication' | 'culprit_power_of_attorney' | 'culprit_registration_certificate' | 'culprit_representative_document' | 'culprit_technical_inspection_report' | 'damage_assessment_report' | 'debit_notes' | 'estimate' | 'examination_report' | 'expertise' | 'indemnification_agreement' | 'insurance_claim' | 'invoice' | 'medical_document' | 'other' | 'payment_order' | 'photo' | 'repair_station_bill' | 'request_for_additional_documents' | 'suspend' | 'terminate'>,
lastVersionOnly?: boolean,
ordering?: string,
page?: number,
pageSize?: number,
type?: Array<'accident_report' | 'additional_examination_report' | 'additional_findings' | 'case' | 'claimant_document' | 'claimant_driver_license' | 'claimant_power_of_attorney' | 'claimant_registration_certificate' | 'claimant_representative_document' | 'compensation_agreement' | 'compensation_estimate' | 'compensation_order' | 'culprit_document' | 'culprit_driver_license' | 'culprit_explication' | 'culprit_power_of_attorney' | 'culprit_registration_certificate' | 'culprit_representative_document' | 'culprit_technical_inspection_report' | 'damage_assessment_report' | 'debit_notes' | 'estimate' | 'examination_report' | 'expertise' | 'indemnification_agreement' | 'insurance_claim' | 'invoice' | 'medical_document' | 'other' | 'payment_order' | 'photo' | 'repair_station_bill' | 'request_for_additional_documents' | 'suspend' | 'terminate'>,
): CancelablePromise<PaginatedClaimDocumentList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/documents/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'exclude_type': excludeType,
        'last_version_only': lastVersionOnly,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'type': type,
      },
    });
  }

  /**
   * Create claim document
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param formData 
   * @returns ClaimDocumentDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiDocumentsCreate(
casePk: string,
formData: ClaimDocumentWrite,
): CancelablePromise<ClaimDocumentDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/{case_pk}/documents/',
      path: {
        'case_pk': casePk,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param id A unique integer value identifying this claim document.
   * @returns ClaimDocumentDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiDocumentsRetrieve(
casePk: string,
id: number,
): CancelablePromise<ClaimDocumentDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/documents/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * Remove claim document. Only unversioned documents can be removed.
   * Base viewset for claim documents. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param id A unique integer value identifying this claim document.
   * @returns void 
   * @throws ApiError
   */
  public static claimsCaseRcaiDocumentsDestroy(
casePk: string,
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/claims/case-rcai/{case_pk}/documents/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
      errors: {
        404: `Document not found`,
      },
    });
  }

  /**
   * @param casePk 
   * @param ordering Ordering fields: created, status
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedRCAIEvaluationList 
   * @throws ApiError
   */
  public static claimsCaseRcaiEvaluationsList(
casePk: string,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedRCAIEvaluationList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/evaluations/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * @param casePk 
   * @param id A unique integer value identifying this case rcai evaluation.
   * @returns RCAIEvaluationDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiEvaluationsRetrieve(
casePk: string,
id: number,
): CancelablePromise<RCAIEvaluationDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/evaluations/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * Read-only base viewset for vehicle examinations. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param ordering Ordering fields: created, scheduled_date, status
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param status * `scheduled` - Programată
 * * `completed` - Efectuată
 * * `rescheduled` - Reprogramată
 * * `non_appearance` - Neprezentare
 * * `re_examination` - Re-examinare
   * @returns PaginatedRCAIExaminationList 
   * @throws ApiError
   */
  public static claimsCaseRcaiExaminationsList(
casePk: string,
ordering?: string,
page?: number,
pageSize?: number,
status?: 'completed' | 'non_appearance' | 're_examination' | 'rescheduled' | 'scheduled',
): CancelablePromise<PaginatedRCAIExaminationList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/examinations/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'status': status,
      },
    });
  }

  /**
   * Read-only base viewset for vehicle examinations. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @param id A unique integer value identifying this vehicle examination.
   * @returns RCAIExaminationDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiExaminationsRetrieve(
casePk: string,
id: number,
): CancelablePromise<RCAIExaminationDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/examinations/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * Events history
   * @param casePk 
   * @param actorType * `user` - User
 * * `broker_user` - Broker User
 * * `system` - System
 * * `api` - External API
   * @param eventType 
   * @param ordering Ordering fields: created, event_type
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @returns PaginatedAuditLogList 
   * @throws ApiError
   */
  public static claimsCaseRcaiHistoryList(
casePk: string,
actorType?: 'api' | 'broker_user' | 'system' | 'user',
eventType?: string,
ordering?: string,
page?: number,
pageSize?: number,
): CancelablePromise<PaginatedAuditLogList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/history/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'actor_type': actorType,
        'event_type': eventType,
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
      },
    });
  }

  /**
   * Events history
   * @param casePk 
   * @param id A unique integer value identifying this audit log.
   * @returns AuditLogDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiHistoryRetrieve(
casePk: string,
id: number,
): CancelablePromise<AuditLogDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/history/{id}/',
      path: {
        'case_pk': casePk,
        'id': id,
      },
    });
  }

  /**
   * List claim parties for a case
   * Base viewset for claim parties. Designed to be nested under a case resource.
 *
 * Subclasses must set `case_model_class` to the parent case model class.
 * The URL kwarg for the parent PK is controlled by `case_pk_kwarg` (default: "case_pk").
   * @param casePk 
   * @returns ClaimParty 
   * @throws ApiError
   */
  public static claimsCaseRcaiPartiesList(
casePk: string,
): CancelablePromise<Array<ClaimParty>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/parties/',
      path: {
        'case_pk': casePk,
      },
    });
  }

  /**
   * Upsert claim party by role
   * Creates or replaces the culprit/claimant party for a claim. Always returns 200.
   * @param casePk 
   * @param role Party role
   * @param requestBody 
   * @returns ClaimParty 
   * @throws ApiError
   */
  public static claimsCaseRcaiPartiesPartialUpdate(
casePk: string,
role: 'claimant' | 'culprit',
requestBody?: PatchedClaimPartyWrite,
): CancelablePromise<ClaimParty> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/claims/case-rcai/{case_pk}/parties/{role}/',
      path: {
        'case_pk': casePk,
        'role': role,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Remove claim party by role
   * DELETE /…/parties/culprit/delete/ or /claimant/delete/
   * @param casePk 
   * @param role Party role
   * @returns void 
   * @throws ApiError
   */
  public static claimsCaseRcaiPartiesDeleteDestroy(
casePk: string,
role: 'claimant' | 'culprit',
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/claims/case-rcai/{case_pk}/parties/{role}/delete/',
      path: {
        'case_pk': casePk,
        'role': role,
      },
      errors: {
        404: `Party not found`,
      },
    });
  }

  /**
   * CaseRCAI reserves
   * @param casePk 
   * @param status * `active` - Active
 * * `deactivated` - Deactivated
 * * `paid` - Paid
   * @returns DamageReserve 
   * @throws ApiError
   */
  public static claimsCaseRcaiReservesList(
casePk: string,
status?: Array<'active' | 'deactivated' | 'paid'>,
): CancelablePromise<Array<DamageReserve>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{case_pk}/reserves/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'status': status,
      },
    });
  }

  /**
   * CaseRCAI reserves
   * @param casePk 
   * @param requestBody 
   * @param status * `active` - Active
 * * `deactivated` - Deactivated
 * * `paid` - Paid
   * @returns DamageReserve 
   * @throws ApiError
   */
  public static claimsCaseRcaiReservesUpdateUpdate(
casePk: string,
requestBody: CoreCaseReserveWrite,
status?: Array<'active' | 'deactivated' | 'paid'>,
): CancelablePromise<Array<DamageReserve>> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/claims/case-rcai/{case_pk}/reserves/update/',
      path: {
        'case_pk': casePk,
      },
      query: {
        'status': status,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiRetrieve(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiAmicableSettlementPdfRetrieve(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/amicable-settlement-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @param requestBody 
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiAntifraudCreate(
id: number,
requestBody: Antifraud,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/{id}/antifraud/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * PATCH /:resource/:id/apply-status/ — Status transition.
   * @param id A unique integer value identifying this case rcai.
   * @param requestBody 
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiApplyStatusPartialUpdate(
id: number,
requestBody?: PatchedRequest,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/claims/case-rcai/{id}/apply-status/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns Transition 
   * @throws ApiError
   */
  public static claimsCaseRcaiCaseTransitionsList(
id: number,
): CancelablePromise<Array<Transition>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/case-transitions/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiCompensationAgreementPdfRetrieve(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/compensation-agreement-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiCompensationOrderPdfRetrieve(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/compensation-order-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiEstimatePdfRetrieve(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/estimate-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiExaminationReportPdfRetrieve(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/examination-report-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiInsuranceClaimPdfRetrieve(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/insurance-claim-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Push this dosar's compensation to SIA (create on first push, otherwise update). 400 if the dosar status is not in the approval/execution stage; SIA errors are surfaced.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiPushCompensationCreate(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/{id}/push-compensation/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Refresh the dosar's policy/insured/vehicle snapshot from fresh RCA API data. 400 if the dosar is closed/paid/refused; 'service_unavailable' if the RCA API is unreachable. Response carries `changed_fields` (empty list when nothing changed).
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiRefreshPolicyDataCreate(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/{id}/refresh-policy-data/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @param repairStation 
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaiRepairShopReferralPdfRetrieve(
id: number,
repairStation: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/repair-shop-referral-pdf/',
      path: {
        'id': id,
      },
      query: {
        'repair_station': repairStation,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiResumeCreate(
id: number,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/{id}/resume/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @param formData 
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiSuspendCreate(
id: number,
formData: Suspend,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/{id}/suspend/',
      path: {
        'id': id,
      },
      formData: formData,
      mediaType: 'multipart/form-data',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaiSuspendPdfRetrieve(
id: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/suspend-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @param id A unique integer value identifying this case rcai.
   * @returns binary 
   * @throws ApiError
   */
  public static claimsCaseRcaiTerminatePdfRetrieve(
id: number,
): CancelablePromise<Blob> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/terminate-pdf/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * GET /:resource/:id/validate-transition/ — Validate transition to status.
   * @param id A unique integer value identifying this case rcai.
   * @param status 
   * @returns ValidationError 
   * @throws ApiError
   */
  public static claimsCaseRcaiValidateTransitionList(
id: number,
status: string,
): CancelablePromise<Array<ValidationError>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/{id}/validate-transition/',
      path: {
        'id': id,
      },
      query: {
        'status': status,
      },
    });
  }

  /**
   * Create from notification.
   * @param requestBody 
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiCreateFromNotificationCreate(
requestBody: CreateFromNotification,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/create-from-notification/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * POST /:resource/new/ — Create case.
   * @param requestBody 
   * @returns ClaimRCAICaseDetail 
   * @throws ApiError
   */
  public static claimsCaseRcaiNewCreate(
requestBody: New,
): CancelablePromise<ClaimRCAICaseDetail> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/case-rcai/new/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * Builds an attachment PDF HttpResponse from a service generate-fn + file-name-fn.
 *
 * The download filename is rendered from ``filename_context`` only — the mixin never
 * inspects ``obj``. Callers supply the mapping, e.g. via ``case_filename_context(case)``.
   * @returns Transition 
   * @throws ApiError
   */
  public static claimsCaseRcaiTransitionsList(): CancelablePromise<Array<Transition>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/case-rcai/transitions/',
    });
  }

  /**
   * ViewSet that delegates write operations to a ``BaseService`` subclass.
 *
 * Set ``service_class`` to the concrete service. ``serializer.save()`` is
 * bypassed — the service receives ``serializer.validated_data`` directly
 * and is responsible for creating/updating/deleting the instance and
 * dispatching domain events.
   * @param ordering Ordering fields: name, created
   * @param page A page number within the paginated result set.
   * @param pageSize Number of results to return per page.
   * @param search Search fields: name, legal_entity__name, legal_entity__identification_number, iban
   * @returns PaginatedRepairStationListList 
   * @throws ApiError
   */
  public static claimsRepairStationsList(
ordering?: string,
page?: number,
pageSize?: number,
search?: string,
): CancelablePromise<PaginatedRepairStationListList> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/repair-stations/',
      query: {
        'ordering': ordering,
        'page': page,
        'page_size': pageSize,
        'search': search,
      },
    });
  }

  /**
   * ViewSet that delegates write operations to a ``BaseService`` subclass.
 *
 * Set ``service_class`` to the concrete service. ``serializer.save()`` is
 * bypassed — the service receives ``serializer.validated_data`` directly
 * and is responsible for creating/updating/deleting the instance and
 * dispatching domain events.
   * @param requestBody 
   * @returns RepairStationModify 
   * @throws ApiError
   */
  public static claimsRepairStationsCreate(
requestBody: RepairStationModify,
): CancelablePromise<RepairStationModify> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/claims/repair-stations/',
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet that delegates write operations to a ``BaseService`` subclass.
 *
 * Set ``service_class`` to the concrete service. ``serializer.save()`` is
 * bypassed — the service receives ``serializer.validated_data`` directly
 * and is responsible for creating/updating/deleting the instance and
 * dispatching domain events.
   * @param id A unique integer value identifying this repair station.
   * @returns RepairStationDetail 
   * @throws ApiError
   */
  public static claimsRepairStationsRetrieve(
id: number,
): CancelablePromise<RepairStationDetail> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/claims/repair-stations/{id}/',
      path: {
        'id': id,
      },
    });
  }

  /**
   * ViewSet that delegates write operations to a ``BaseService`` subclass.
 *
 * Set ``service_class`` to the concrete service. ``serializer.save()`` is
 * bypassed — the service receives ``serializer.validated_data`` directly
 * and is responsible for creating/updating/deleting the instance and
 * dispatching domain events.
   * @param id A unique integer value identifying this repair station.
   * @param requestBody 
   * @returns RepairStation 
   * @throws ApiError
   */
  public static claimsRepairStationsPartialUpdate(
id: number,
requestBody?: PatchedRepairStation,
): CancelablePromise<RepairStation> {
    return __request(OpenAPI, {
      method: 'PATCH',
      url: '/claims/repair-stations/{id}/',
      path: {
        'id': id,
      },
      body: requestBody,
      mediaType: 'application/json',
    });
  }

  /**
   * ViewSet that delegates write operations to a ``BaseService`` subclass.
 *
 * Set ``service_class`` to the concrete service. ``serializer.save()`` is
 * bypassed — the service receives ``serializer.validated_data`` directly
 * and is responsible for creating/updating/deleting the instance and
 * dispatching domain events.
   * @param id A unique integer value identifying this repair station.
   * @returns void 
   * @throws ApiError
   */
  public static claimsRepairStationsDestroy(
id: number,
): CancelablePromise<void> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/claims/repair-stations/{id}/',
      path: {
        'id': id,
      },
    });
  }

}
